/* eslint-disable react/no-unescaped-entities */
import React, { useMemo, useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Phone } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { getEffectiveRef } from "@/lib/refTracker";
import { markToolCompleted } from "@/lib/journeyTracker";
import { saveJourneyRef } from "@/lib/journeyRef";
import { toast } from "sonner";

const STORY_MIN = 80;
const STORY_MAX = 2500;
const CONSENT_VERSION = "orizzonti-consent-2026-08-16";

const STRUCTURED = [
  {
    key: "situazione_attuale",
    kind: "multi",
    title: "Qual è oggi la situazione dell'immobile?",
    hint: "Puoi selezionare più opzioni.",
    options: [
      { value: "abito_io", label: "Ci vivo io o un familiare" },
      { value: "vuoto", label: "È vuoto" },
      { value: "affittato", label: "È affittato" },
      { value: "eredita_comproprieta", label: "È ereditato o in comproprietà" },
      { value: "richiede_lavori", label: "Richiede lavori" },
      { value: "altro", label: "Altro" },
    ],
  },
  {
    key: "risultato_prioritario",
    kind: "single",
    title: "Quale risultato conta di più per te?",
    options: [
      { value: "miglior_risultato_economico", label: "Ottenere il miglior risultato economico" },
      { value: "rendita", label: "Generare una rendita" },
      { value: "rapidita", label: "Agire rapidamente" },
      { value: "ridurre_rischi", label: "Ridurre rischi e problemi" },
      { value: "conservare_famiglia", label: "Conservarlo per la famiglia" },
      { value: "altro", label: "Altro" },
    ],
  },
  {
    key: "orizzonte_temporale",
    kind: "single",
    title: "Entro quando vorresti vedere un cambiamento concreto?",
    options: [
      { value: "3_mesi", label: "Entro 3 mesi" },
      { value: "12_mesi", label: "Entro 12 mesi" },
      { value: "24_mesi", label: "Entro 24 mesi" },
      { value: "esploro", label: "Sto ancora esplorando" },
    ],
  },
  {
    key: "chi_decide",
    kind: "multi",
    title: "Chi partecipa alla decisione?",
    hint: "Puoi selezionare più opzioni.",
    options: [
      { value: "decido_io", label: "Decido io" },
      { value: "partner_famiglia", label: "Partner o famiglia" },
      { value: "comproprietari_eredi", label: "Comproprietari o eredi" },
      { value: "professionisti", label: "Devo ancora confrontarmi con professionisti" },
    ],
  },
  {
    key: "limite_da_proteggere",
    kind: "single",
    title: "Quale limite vuoi proteggere maggiormente?",
    options: [
      { value: "non_perdere_valore", label: "Non perdere valore" },
      { value: "non_investire", label: "Non investire altro denaro" },
      { value: "evitare_lavori", label: "Evitare lavori e burocrazia" },
      { value: "non_aspettare", label: "Non aspettare troppo" },
      { value: "conservare_uso", label: "Conservare l'uso o il valore familiare" },
      { value: "altro", label: "Altro" },
    ],
  },
];

const ALTRO_KEYS = new Set(["situazione_attuale", "risultato_prioritario", "limite_da_proteggere"]);

function makeIdempotencyKey() {
  return `oriz-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function OrizzontiPage() {
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const referral = refFromUrl || getEffectiveRef() || "";

  // Phases: intro -> story -> q0..q4 -> contact -> submitting -> result | error
  const [phase, setPhase] = useState("intro");
  const [story, setStory] = useState("");
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [altro, setAltro] = useState({});
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [consentService, setConsentService] = useState(false);
  const [consentNewsletter, setConsentNewsletter] = useState(false);
  const [idempotencyKey] = useState(makeIdempotencyKey);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null); // {message, journey_id?}

  const currentQ = STRUCTURED[qIndex];
  const currentValue = currentQ ? answers[currentQ.key] : null;
  const currentAltro = currentQ ? (altro[currentQ.key] || "") : "";

  const canContinueQuestion = useMemo(() => {
    if (!currentQ) return false;
    const v = currentValue;
    if (currentQ.kind === "multi") {
      if (!Array.isArray(v) || v.length === 0) return false;
      if (v.includes("altro") && currentAltro.trim().length < 3) return false;
      return true;
    }
    if (!v) return false;
    if (v === "altro" && currentAltro.trim().length < 3) return false;
    return true;
  }, [currentQ, currentValue, currentAltro]);

  const emailValid = /^\S+@\S+\.\S+$/.test((email || "").trim());
  const canSubmit = nome.trim().length >= 2 && emailValid && consentService && !submitting;

  const toggleMulti = (val) => {
    const cur = Array.isArray(currentValue) ? currentValue : [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setAnswers((a) => ({ ...a, [currentQ.key]: next }));
  };
  const setSingle = (val) => setAnswers((a) => ({ ...a, [currentQ.key]: val }));

  const goStory = () => setPhase("story");
  const goStructured = () => {
    const s = story.trim();
    if (s.length < STORY_MIN) return toast.error(`Il racconto deve avere almeno ${STORY_MIN} caratteri.`);
    if (s.length > STORY_MAX) return toast.error(`Il racconto è troppo lungo (max ${STORY_MAX}).`);
    setPhase("questions");
    setQIndex(0);
  };
  const nextQ = () => {
    if (qIndex < STRUCTURED.length - 1) setQIndex((i) => i + 1);
    else setPhase("contact");
  };
  const backQ = () => {
    if (phase === "contact") {
      setPhase("questions");
      setQIndex(STRUCTURED.length - 1);
      return;
    }
    if (qIndex > 0) setQIndex((i) => i - 1);
    else setPhase("story");
  };

  const submit = async () => {
    // Double-click guard: submitting flag + local guard prevent duplicate posts.
    if (submitting) return;
    setSubmitting(true);
    setErrorInfo(null);
    setPhase("submitting");
    try {
      const r = await api.post("/orizzonti/submit", {
        story: story.trim(),
        answers,
        altro,
        nome: nome.trim(),
        email: email.trim(),
        consent_service: consentService,
        consent_service_version: CONSENT_VERSION,
        consent_newsletter: consentNewsletter,
        referral_code_origine: referral || null,
        idempotency_key: idempotencyKey,
      });
      setResult(r.data);
      // Persist signed journey reference for downstream tools (ValutaPro, PartenzaPro).
      // Uses the EXISTING signed token returned by the backend: no new endpoint,
      // no new secret, no PII in localStorage.
      if (r.data?.journey_id && r.data?.cta_token) {
        saveJourneyRef({ j: r.data.journey_id, t: r.data.cta_token });
      }
      setPhase("result");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Errore temporaneo. Riprova.";
      const jid = e?.response?.data?.journey_id || null;
      setErrorInfo({
        message: detail,
        journey_id: jid,
        recoverable: status === 502 || status === 504 || !status,
      });
      setPhase("error");
    } finally {
      setSubmitting(false);
    }
  };

  const retrySubmit = async () => {
    // Idempotent retry: reuses the same idempotency_key, so backend returns
    // the existing journey without creating a new lead or duplicate AI call.
    if (submitting) return;
    setSubmitting(true);
    setPhase("submitting");
    try {
      // If we have a journey_id from a prior 502, try /orizzonti/retry first.
      if (errorInfo?.journey_id) {
        try {
          const rr = await api.post("/orizzonti/retry", { journey_id: errorInfo.journey_id });
          if (rr?.data?.status === "completed") {
            setResult({
              journey_id: errorInfo.journey_id,
              status: "completed",
              result: rr.data.result,
              cta_url: null,
              cta_label: "Continua",
            });
            if (rr.data?.cta_token) {
              saveJourneyRef({ j: errorInfo.journey_id, t: rr.data.cta_token });
            }
            setPhase("result");
            return;
          }
        } catch (_) { /* fall through to full submit retry */ }
      }
      const r = await api.post("/orizzonti/submit", {
        story: story.trim(),
        answers,
        altro,
        nome: nome.trim(),
        email: email.trim(),
        consent_service: consentService,
        consent_service_version: CONSENT_VERSION,
        consent_newsletter: consentNewsletter,
        referral_code_origine: referral || null,
        idempotency_key: idempotencyKey,
      });
      setResult(r.data);
      if (r.data?.journey_id && r.data?.cta_token) {
        saveJourneyRef({ j: r.data.journey_id, t: r.data.cta_token });
      }
      setPhase("result");
    } catch (e) {
      setErrorInfo((ei) => ({
        ...(ei || {}),
        message: e?.response?.data?.detail || "Errore temporaneo. Riprova più tardi.",
        recoverable: true,
      }));
      setPhase("error");
    } finally {
      setSubmitting(false);
    }
  };

  // ============================== RENDER ==============================
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <header className="flex items-center justify-between mb-8">
          <Link
            to="/app"
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
            data-testid="orizzonti-back-app"
          >
            <ArrowLeft size={16} /> Percorsi
          </Link>
          <Logo size={22} />
          <Link to="/scopri" className="text-sm text-gray-500 hover:text-[#B40101]">Come funziona</Link>
        </header>

        {phase === "intro" && <IntroScreen onStart={goStory} />}
        {phase === "story" && (
          <StoryScreen
            story={story}
            setStory={setStory}
            onContinue={goStructured}
            onBack={() => setPhase("intro")}
          />
        )}
        {phase === "questions" && currentQ && (
          <QuestionScreen
            q={currentQ}
            index={qIndex}
            total={STRUCTURED.length}
            value={currentValue}
            altroText={currentAltro}
            onAltroChange={(v) => setAltro((a) => ({ ...a, [currentQ.key]: v }))}
            onToggle={toggleMulti}
            onSelect={setSingle}
            canNext={canContinueQuestion}
            onBack={backQ}
            onNext={nextQ}
          />
        )}
        {phase === "contact" && (
          <ContactScreen
            nome={nome} setNome={setNome}
            email={email} setEmail={setEmail}
            consentService={consentService} setConsentService={setConsentService}
            consentNewsletter={consentNewsletter} setConsentNewsletter={setConsentNewsletter}
            onBack={backQ}
            onSubmit={submit}
            canSubmit={canSubmit}
            submitting={submitting}
          />
        )}
        {phase === "submitting" && <SubmittingScreen />}
        {phase === "error" && (
          <ErrorScreen
            info={errorInfo}
            onRetry={retrySubmit}
            onBackToContact={() => setPhase("contact")}
            submitting={submitting}
          />
        )}
        {phase === "result" && result && (
          <ResultScreen data={result.result} nome={nome} email={email} journeyId={result.journey_id} referral={referral} onRetry={retrySubmit} submitting={submitting} />
        )}
      </div>
    </div>
  );
}

// ============================== SCREENS ==============================
function IntroScreen({ onStart }) {
  return (
    <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm">
      <p className="text-[#B40101] font-bold uppercase tracking-[0.16em] text-xs" data-testid="orizzonti-eyebrow">
        ORIZZONTIPRO · PRIMO PASSO
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0A0A0A] mt-3 leading-tight" data-testid="orizzonti-intro-title">
        Ogni immobile contiene un progetto. Cominciamo dal tuo.
      </h1>
      <p className="text-[#666] mt-4 leading-relaxed" data-testid="orizzonti-intro-body">
        Raccontare ciò che immagini ci aiuta a distinguere una semplice idea da una direzione realmente percorribile. Servono il punto da cui parti e il risultato che vorresti raggiungere.
      </p>
      <p className="text-sm text-[#666] mt-4 leading-relaxed">
        Servono circa tre minuti. Il tuo racconto verrà utilizzato soltanto per preparare l'orientamento richiesto.
      </p>
      <button
        onClick={onStart}
        className="mt-8 vg-btn-primary"
        style={{ background: "#B40101" }}
        data-testid="orizzonti-start-btn"
      >
        Racconta il tuo scenario <ArrowRight size={18} />
      </button>
    </div>
  );
}

function StoryScreen({ story, setStory, onContinue, onBack }) {
  const len = story.length;
  const under = len < STORY_MIN;
  const over = len > STORY_MAX;
  return (
    <div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 mb-4" data-testid="orizzonti-story-back">
        <ArrowLeft size={14} /> Indietro
      </button>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A0A0A]" data-testid="orizzonti-story-title">Descrivi il tuo scenario ideale.</h2>
      <p className="text-[#666] mt-3 leading-relaxed">
        Immagina che, tra uno o due anni, la situazione del tuo immobile si sia risolta nel modo migliore per te. Che cosa è successo? Come utilizzi l'immobile? Che cosa ti farebbe dire: "Ho preso la decisione giusta"? Racconta anche ciò che oggi ti frena o ti preoccupa. Non servono termini tecnici.
      </p>
      <textarea
        className="w-full mt-6 min-h-[200px] p-4 rounded-lg border border-gray-200 focus:border-[#B40101] focus:outline-none text-[15px] leading-relaxed"
        value={story}
        onChange={(e) => setStory(e.target.value)}
        maxLength={STORY_MAX + 50}
        data-testid="orizzonti-story-textarea"
        placeholder="Racconta la tua situazione…"
      />
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className={under ? "text-[#B40101]" : "text-gray-500"} data-testid="orizzonti-story-counter">
          {len}/{STORY_MAX} caratteri {under ? `· minimo ${STORY_MIN}` : ""}
        </span>
        {over && <span className="text-[#B40101]">Troppo lungo</span>}
      </div>
      <div className="mt-8 flex justify-end">
        <button
          className="vg-btn-primary"
          onClick={onContinue}
          disabled={under || over}
          data-testid="orizzonti-story-continue"
        >
          Continua <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function QuestionScreen({ q, index, total, value, altroText, onAltroChange, onToggle, onSelect, canNext, onBack, onNext }) {
  const showAltro = ALTRO_KEYS.has(q.key) && (
    (q.kind === "multi" && Array.isArray(value) && value.includes("altro")) ||
    (q.kind === "single" && value === "altro")
  );
  return (
    <div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 mb-4" data-testid={`orizzonti-q-back-${q.key}`}>
        <ArrowLeft size={14} /> Indietro
      </button>
      <p className="text-xs uppercase tracking-widest text-gray-400 mb-3" data-testid="orizzonti-q-progress">
        Domanda {index + 1} di {total}
      </p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A0A0A] leading-tight" data-testid={`orizzonti-q-title-${q.key}`}>
        {q.title}
      </h2>
      {q.hint && <p className="text-sm text-gray-500 mt-2">{q.hint}</p>}

      <div className="space-y-3 mt-6">
        {q.options.map((o) => {
          const selected = q.kind === "multi"
            ? (Array.isArray(value) && value.includes(o.value))
            : value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => (q.kind === "multi" ? onToggle(o.value) : onSelect(o.value))}
              className={`w-full text-left p-4 rounded-lg border-2 transition ${selected ? "border-[#B40101] bg-[#FFF5F5]" : "border-gray-200 hover:border-gray-300"}`}
              data-testid={`orizzonti-opt-${q.key}-${o.value}`}
            >
              <span className={`inline-block w-5 h-5 mr-3 align-middle rounded ${q.kind === "multi" ? "" : "rounded-full"} border-2 ${selected ? "bg-[#B40101] border-[#B40101]" : "border-gray-300"}`} />
              <span className="align-middle font-medium">{o.label}</span>
            </button>
          );
        })}
      </div>

      {showAltro && (
        <div className="mt-4">
          <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Specifica:</label>
          <input
            className="vg-input"
            value={altroText}
            onChange={(e) => onAltroChange(e.target.value)}
            maxLength={300}
            data-testid={`orizzonti-altro-${q.key}`}
            placeholder="Descrivi in una frase…"
          />
        </div>
      )}

      <div className="mt-10 flex justify-end">
        <button
          className="vg-btn-primary"
          disabled={!canNext}
          onClick={onNext}
          data-testid={`orizzonti-next-${q.key}`}
        >
          Continua <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function ContactScreen({ nome, setNome, email, setEmail, consentService, setConsentService, consentNewsletter, setConsentNewsletter, onBack, onSubmit, canSubmit, submitting }) {
  return (
    <div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 mb-4" data-testid="orizzonti-contact-back">
        <ArrowLeft size={14} /> Indietro
      </button>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A0A0A]" data-testid="orizzonti-contact-title">
        Prepara il tuo orientamento
      </h2>
      <p className="text-[#666] mt-3 leading-relaxed">
        Ti invieremo la lettura del tuo scenario e i passi consigliati. Nessuna newsletter automatica e nessuna telefonata senza una tua richiesta.
      </p>

      <div className="space-y-5 mt-6">
        <div>
          <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Nome</label>
          <input className="vg-input" value={nome} onChange={(e) => setNome(e.target.value)} data-testid="orizzonti-nome-input" placeholder="Il tuo nome" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Email</label>
          <input className="vg-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="orizzonti-email-input" placeholder="tua@email.it" />
        </div>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="orizzonti-consent-service-wrap">
          <input
            type="checkbox"
            className="mt-1"
            checked={consentService}
            onChange={(e) => setConsentService(e.target.checked)}
            data-testid="orizzonti-consent-service"
          />
          <span className="text-sm text-[#0A0A0A] leading-relaxed">
            Ho preso visione dell'<Link to="/privacy" className="underline">informativa privacy</Link> e acconsento al trattamento dei dati necessari a generare l'analisi e condividerla con il Property Advisor associato al referral.
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="orizzonti-consent-newsletter-wrap">
          <input
            type="checkbox"
            className="mt-1"
            checked={consentNewsletter}
            onChange={(e) => setConsentNewsletter(e.target.checked)}
            data-testid="orizzonti-consent-newsletter"
          />
          <span className="text-sm text-[#0A0A0A] leading-relaxed">
            Desidero ricevere aggiornamenti e consigli via email (facoltativo).
          </span>
        </label>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          className="vg-btn-primary"
          disabled={!canSubmit}
          onClick={onSubmit}
          data-testid="orizzonti-submit"
        >
          {submitting ? (<><Loader2 className="animate-spin" size={18} /> Elaboro…</>) : (<>Prepara il mio orientamento <ArrowRight size={18} /></>)}
        </button>
      </div>
    </div>
  );
}

function SubmittingScreen() {
  return (
    <div className="bg-white rounded-2xl p-10 shadow-sm text-center" data-testid="orizzonti-submitting">
      <Loader2 className="animate-spin mx-auto text-[#B40101]" size={40} />
      <p className="mt-4 text-[#0A0A0A] font-semibold">Stiamo componendo il tuo orientamento…</p>
      <p className="mt-2 text-sm text-gray-500">Serve qualche secondo. Non chiudere questa pagina.</p>
    </div>
  );
}

function ErrorScreen({ info, onRetry, onBackToContact, submitting }) {
  const recoverable = info?.recoverable !== false;
  return (
    <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm" data-testid="orizzonti-error-screen">
      <div className="flex items-center gap-2 text-[#B40101] font-bold uppercase tracking-[0.16em] text-xs">
        Ops · qualcosa non è andato a buon fine
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A0A0A] mt-3">
        Non siamo riusciti a preparare l'orientamento.
      </h2>
      <p className="text-[#666] mt-3 leading-relaxed" data-testid="orizzonti-error-message">
        {info?.message || "Errore temporaneo. Riprova."}
      </p>
      <p className="text-sm text-gray-500 mt-2">
        Il tuo racconto e le tue risposte sono state conservate. Puoi riprovare senza dover ricompilare.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        {recoverable && (
          <button
            className="vg-btn-primary"
            onClick={onRetry}
            disabled={submitting}
            data-testid="orizzonti-error-retry"
          >
            {submitting ? (<><Loader2 className="animate-spin" size={18}/> Riprovo…</>) : (<>Riprova <ArrowRight size={18}/></>)}
          </button>
        )}
        <button
          className="text-sm text-[#666] underline"
          onClick={onBackToContact}
          data-testid="orizzonti-error-back"
        >
          Modifica i miei dati
        </button>
      </div>
    </div>
  );
}

function ResultScreen({ data, nome, email, journeyId, referral, onRetry, submitting }) {
  const [callOpen, setCallOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const missing = !data || !data.orientation_profile || !Array.isArray(data.next_three_steps) || data.next_three_steps.length !== 3;

  useEffect(() => {
    if (!missing && email) {
      markToolCompleted("orizzonti", { email, nome, journey_id: journeyId });
    }
    // Only run when result becomes available
  }, [missing, email, nome, journeyId]);

  if (missing) {
    return (
      <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm" data-testid="orizzonti-result-incomplete">
        <h2 className="font-display text-2xl font-bold text-[#0A0A0A]">Il tuo orientamento è incompleto.</h2>
        <p className="text-[#666] mt-3 leading-relaxed">La risposta ricevuta non è nel formato atteso. Puoi rigenerarla senza ricompilare le risposte.</p>
        <button className="vg-btn-primary mt-6" onClick={onRetry} disabled={submitting} data-testid="orizzonti-result-retry">
          {submitting ? (<><Loader2 className="animate-spin" size={18}/> Riprovo…</>) : (<>Rigenera l'orientamento <ArrowRight size={18}/></>)}
        </button>
      </div>
    );
  }

  const story = (data?.story_summary || "").trim();
  const direction = (data?.coherent_direction || "").trim();
  const points = Array.isArray(data?.points_to_clarify) ? data.points_to_clarify : [];
  const decisivePoint = (points[0] || "").trim();

  const refQ = referral ? `&ref=${encodeURIComponent(referral)}` : "";
  const nextToolUrl = `/valuta?email=${encodeURIComponent(email || "")}${refQ}`;

  const handleResend = async () => {
    if (resendBusy || !email) return;
    setResendBusy(true);
    try {
      await api.post("/email/resend", { tool: "orizzonti", email, journey_id: journeyId });
      toast.success("Email rispedita. Controlla la tua casella.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile rispedire ora. Riprova tra qualche minuto.");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="orizzonti-result">
      <div className="flex items-center gap-2 text-[#B40101] font-bold uppercase tracking-[0.16em] text-xs">
        <CheckCircle2 size={18} /> Orientamento pronto
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0A0A0A] mt-3" data-testid="orizzonti-result-title">
        Ciao {nome}, ti abbiamo scritto.
      </h2>
      <p className="text-[#666] mt-3 leading-relaxed" data-testid="orizzonti-result-email-msg">
        Il tuo orientamento completo, con i punti da verificare e le azioni consigliate, è nella tua email
        {email ? <> (<strong className="text-[#0A0A0A]">{email}</strong>)</> : null}.
      </p>

      {story && (
        <div className="mt-6 p-4 rounded-xl bg-[#FAF7F2] border border-[#EDE4D3]" data-testid="orizzonti-insight-story">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#B40101] mb-1">Racconto interpretato</div>
          <p className="text-[#0A0A0A] leading-relaxed text-sm">{story}</p>
        </div>
      )}
      {direction && (
        <div className="mt-3 p-4 rounded-xl bg-[#F5F5F5] border border-[#E5E5E5]" data-testid="orizzonti-insight-direction">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#B40101] mb-1">Direzione più coerente</div>
          <p className="text-[#0A0A0A] leading-relaxed text-sm">{direction}</p>
        </div>
      )}
      {decisivePoint && (
        <div className="mt-3 p-4 rounded-xl bg-white border border-[#E5E5E5]" data-testid="orizzonti-insight-point">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#B40101] mb-1">Un punto decisivo da chiarire</div>
          <p className="text-[#0A0A0A] leading-relaxed text-sm">{decisivePoint}</p>
        </div>
      )}

      <div className="mt-6 p-4 rounded-lg bg-[#FFFAF0] border-l-4 border-[#B40101] text-sm text-[#0A0A0A] leading-relaxed" data-testid="orizzonti-email-notice">
        Punti da verificare, azioni consigliate e il prossimo strumento consigliato li trovi nell'email che ti abbiamo appena mandato. Se non arriva entro pochi minuti, controlla lo spam o
        <button
          className="ml-1 underline text-[#B40101] font-semibold disabled:opacity-60"
          onClick={handleResend}
          disabled={resendBusy || !email}
          data-testid="orizzonti-resend-btn"
        >
          {resendBusy ? "rispedizione in corso…" : "rispedisci l'email"}
        </button>.
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <a
          href={nextToolUrl}
          className="inline-flex items-center justify-center gap-2 bg-[#B40101] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#8E0101] transition-colors"
          data-testid="orizzonti-cta-next-tool"
        >
          Continua con ValutaPro <ArrowRight size={16} />
        </a>
        <button
          type="button"
          onClick={() => setCallOpen((v) => !v)}
          className="inline-flex items-center justify-center gap-2 border border-[#B40101] text-[#B40101] font-semibold px-5 py-3 rounded-lg hover:bg-[#FFF5F5] transition-colors"
          data-testid="orizzonti-cta-talk-pa"
        >
          <Phone size={16} /> Voglio parlare con il mio Property Advisor
        </button>
      </div>

      {callOpen && (
        <CallRequestForm
          journeyId={journeyId}
          onDone={() => { toast.success("Richiesta inviata."); setCallOpen(false); }}
        />
      )}

      <div className="mt-8 p-3 rounded-lg bg-[#F5F5F5] text-xs text-[#666] leading-relaxed">
        Questo orientamento nasce dalle informazioni che hai condiviso. Non sostituisce una valutazione tecnica, legale, fiscale o immobiliare svolta da un professionista abilitato.
      </div>
    </div>
  );
}

function CallRequestForm({ journeyId, onDone }) {
  const [tel, setTel] = useState("");
  const [chan, setChan] = useState("whatsapp");
  const [fascia, setFascia] = useState("pomeriggio");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!journeyId) {
      toast.error("Analisi non collegata correttamente. Ricarica la pagina.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/orizzonti/call-request", {
        journey_id: journeyId,
        telefono: tel.trim(),
        canale_preferito: chan,
        fascia_oraria: fascia,
        consenso_contatto: consent,
      });
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio richiesta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 p-4 rounded-lg border border-gray-200" data-testid="orizzonti-call-form">
      <p className="text-sm text-[#0A0A0A] mb-3">Un Property Advisor ti ricontatterà con il canale che preferisci.</p>
      <input className="vg-input" placeholder="Telefono" value={tel} onChange={(e) => setTel(e.target.value)} data-testid="orizzonti-call-tel" />
      <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
        {["whatsapp","chiamata","email"].map(v => (
          <button key={v} onClick={() => setChan(v)} data-testid={`orizzonti-call-chan-${v}`}
            className={`p-2 rounded border ${chan===v?"border-[#B40101] bg-[#FFF5F5]":"border-gray-200"}`}>
            {v}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
        {["mattina","pomeriggio","sera"].map(v => (
          <button key={v} onClick={() => setFascia(v)} data-testid={`orizzonti-call-fascia-${v}`}
            className={`p-2 rounded border ${fascia===v?"border-[#B40101] bg-[#FFF5F5]":"border-gray-200"}`}>
            {v}
          </button>
        ))}
      </div>
      <label className="flex items-start gap-2 mt-3 text-sm">
        <input type="checkbox" checked={consent} onChange={(e)=>setConsent(e.target.checked)} data-testid="orizzonti-call-consent" />
        Acconsento a essere ricontattato.
      </label>
      <button
        onClick={submit}
        disabled={!consent || tel.length < 6 || busy}
        className="mt-3 vg-btn-primary"
        data-testid="orizzonti-call-submit"
      >
        {busy ? <Loader2 className="animate-spin" size={16}/> : "Richiedi telefonata"}
      </button>
    </div>
  );
}
